import React from 'react';
import { Card, Typography, Statistic, Row, Col, Skeleton } from 'antd';
import { TrophyOutlined, PercentageOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const { Text } = Typography;

const STAGE_COLORS = {
  QUALIFICATION: '#1890ff',
  NEEDS_ANALYSIS: '#722ed1',
  VALUE_PROPOSITION: '#13c2c2',
  IDENTIFY_DECISION_MAKERS: '#faad14',
  PROPOSAL_PRICE_QUOTE: '#fa8c16',
  NEGOTIATION_REVIEW: '#eb2f96',
  CLOSED_WON: '#52c41a',
  CLOSED_LOST: '#ff4d4f',
  CLOSED_LOST_TO_COMPETITION: '#cf1322',
};

const PipelineOverview = ({ data, loading }) => {
  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value?.toLocaleString('en-IN') || 0}`;
  };

  const formatYAxis = (value) => {
    if (value >= 10000000) {
      return `${(value / 10000000).toFixed(0)}Cr`;
    } else if (value >= 100000) {
      return `${(value / 100000).toFixed(0)}L`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value;
  };

  if (loading) {
    return (
      <Card size="small" title={<Text strong>Sales Pipeline</Text>}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  // Prepare chart data - show top stages by value
  const chartData = (data?.by_stage || [])
    .filter(item => !item.stage?.startsWith('CLOSED'))
    .slice(0, 6)
    .map(item => ({
      name: item.stage_label?.length > 12
        ? item.stage_label.substring(0, 12) + '...'
        : item.stage_label,
      fullName: item.stage_label,
      value: item.value,
      count: item.count,
      fill: STAGE_COLORS[item.stage] || '#1890ff'
    }));

  return (
    <Card size="small" title={<Text strong>Sales Pipeline</Text>}>
      {/* Summary Stats */}
      <Row gutter={[16, 8]} style={{ marginBottom: '16px' }}>
        <Col xs={8}>
          <Statistic
            title={<span style={{ fontSize: '12px' }}>Pipeline Value</span>}
            value={data?.summary?.total_value || 0}
            prefix="₹"
            precision={0}
            valueStyle={{ fontSize: '18px', color: '#1890ff' }}
            formatter={(value) => formatCurrency(value).replace('₹', '')}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title={<span style={{ fontSize: '12px' }}>Won Value</span>}
            value={data?.summary?.won_value || 0}
            prefix={<TrophyOutlined style={{ color: '#52c41a' }} />}
            precision={0}
            valueStyle={{ fontSize: '18px', color: '#52c41a' }}
            formatter={(value) => formatCurrency(value).replace('₹', '')}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title={<span style={{ fontSize: '12px' }}>Win Rate</span>}
            value={data?.summary?.win_rate || 0}
            suffix="%"
            prefix={<PercentageOutlined />}
            valueStyle={{
              fontSize: '18px',
              color: (data?.summary?.win_rate || 0) >= 50 ? '#52c41a' : '#faad14'
            }}
          />
        </Col>
      </Row>

      {/* Pipeline Bar Chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 10 }}
              width={45}
            />
            <Tooltip
              formatter={(value, name, props) => [
                `₹${value?.toLocaleString('en-IN')} (${props.payload.count} deals)`,
                props.payload.fullName
              ]}
              contentStyle={{ fontSize: '12px' }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div style={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Text type="secondary">No pipeline data available for this period</Text>
        </div>
      )}
    </Card>
  );
};

export default PipelineOverview;
